<script>
import { computed } from 'vue';
import { frontendURL } from '../../../helper/URLHelper';
import SecondaryNavItem from './SecondaryNavItem.vue';
import AccountContext from './AccountContext.vue';
import { mapGetters } from 'vuex';
import { FEATURE_FLAGS, REDIS_FEATURE_FLAGS } from '../../../featureFlags';
import {
  getUserPermissions,
  hasPermissions,
} from '../../../helper/permissionsHelper';
import { routesWithPermissions } from '../../../routes';
import { useColumnResize } from 'dashboard/composables/useColumnResize';
import { useConfig } from 'dashboard/composables/useConfig';
import { useMapGetter } from 'dashboard/composables/store';
import { overlayScrollbar } from 'dashboard/directives/overlayScrollbar';
import {
  isSidebarItemVisible,
  withSectionTitles,
} from 'dashboard/helper/sidebarNavHelper';

export default {
  components: {
    AccountContext,
    SecondaryNavItem,
  },
  directives: { overlayScrollbar },
  setup() {
    // RTL is per-account and doesn't flip mid-session — read it once.
    const isRTL = useMapGetter('accounts/isRTL').value;
    const { width, onResizeStart } = useColumnResize({
      minWidth: 180, // allow shrinking below the default 14rem clamp floor for a compact nav
      maxWidth: 288, // cap at the default clamp max (18rem) — resize can shrink, not grow past default
      storageKey: 'secondary_sidebar_width',
      isRTL,
    });
    // Secondary is a flex CHILD (flex-shrink-0) of `<aside class="flex">`, so
    // flex-basis governs its main size. null when unset → the w-[clamp(...)]
    // default governs; inline flex-basis wins once the user drags.
    const sidebarStyle = computed(() =>
      width.value == null ? null : { flexBasis: `${width.value}px` }
    );
    const { isEnterprise } = useConfig();
    return { sidebarStyle, onSidebarResizeStart: onResizeStart, isEnterprise };
  },
  props: {
    accountId: {
      type: Number,
      default: 0,
    },
    labels: {
      type: Array,
      default: () => [],
    },
    inboxes: {
      type: Array,
      default: () => [],
    },
    teams: {
      type: Array,
      default: () => [],
    },
    customViews: {
      type: Array,
      default: () => [],
    },
    menuConfig: {
      type: Object,
      default: () => {},
    },
    currentUser: {
      type: Object,
      default: () => {},
    },
    isOnChatwootCloud: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters({
      account: 'getCurrentAccount',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      isEmailCampaignsEnabled: 'accounts/isEmailCampaignsEnabled',
      isNewFeatureFlagEnabled: 'isNewFeatureFlagEnabled',
      globalConfig: 'globalConfig/get',
    }),
    isClaudiaOnly() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        REDIS_FEATURE_FLAGS.CLAUDIA_ONLY
      );
    },
    hasSecondaryMenu() {
      return this.menuConfig.menuItems && this.menuConfig.menuItems.length;
    },
    contactFilters() {
      return this.customViews.filter(view => view.filter_type === 'contact');
    },
    conversationFilters() {
      // Sort A→Z, numeric-aware so a "1." / "2." / "10." name prefix orders
      // naturally (no zero-padding needed). Lets operators control the order of
      // workflow-stage filters just by naming them.
      return this.customViews
        .filter(view => view.filter_type === 'conversation')
        .sort((a, b) =>
          (a.name || '').localeCompare(b.name || '', undefined, {
            numeric: true,
          })
        );
    },
    accessibleMenuItems() {
      const menuItems = this.menuConfig?.menuItems ?? [];
      const menuItemsFilteredByPermissions = menuItems.filter(menuItem => {
        const userPermissions = getUserPermissions(
          this.currentUser,
          this.accountId
        );
        return hasPermissions(
          routesWithPermissions[menuItem.toStateName],
          userPermissions
        );
      });
      return menuItemsFilteredByPermissions.filter(item => {
        // Combined predicates so an item carrying multiple flags (e.g. cloud-only AND
        // email-campaigns-gated) is filtered by ALL of them, not just the first matching.
        if (item.showOnlyOnCloud && !this.isOnChatwootCloud) return false;
        if (
          item.requiresEmailCampaigns &&
          !this.isEmailCampaignsEnabled(this.accountId)
        )
          return false;
        // Same global switch that hides the Templates tab in inbox settings —
        // both entry points must disappear together.
        if (
          item.requiresWhatsappTemplates &&
          this.globalConfig.hideWhatsappTemplateConfig
        )
          return false;
        return true;
      });
    },
    sectionedMenuItems() {
      return withSectionTitles(this.accessibleMenuItems, menuItem =>
        isSidebarItemVisible(menuItem, {
          accountId: this.account?.id,
          globalConfig: this.globalConfig,
          isEnterprise: this.isEnterprise,
          isFeatureEnabledonAccount: this.isFeatureEnabledonAccount,
          isNewFeatureFlagEnabled: this.isNewFeatureFlagEnabled,
        })
      );
    },
    inboxSection() {
      return {
        icon: 'folder',
        label: 'INBOXES',
        hasSubMenu: true,
        newLink:
          this.showNewLink(FEATURE_FLAGS.INBOX_MANAGEMENT) &&
          !this.isClaudiaOnly,
        newLinkTag: 'NEW_INBOX',
        key: 'inbox',
        toState: frontendURL(`accounts/${this.accountId}/settings/inboxes/new`),
        toStateName: 'settings_inbox_new',
        newLinkRouteName: 'settings_inbox_new',
        children: this.inboxes
          .map(inbox => ({
            id: inbox.id,
            label: inbox.name,
            truncateLabel: true,
            toState: frontendURL(
              `accounts/${this.accountId}/inbox/${inbox.id}`
            ),
            type: inbox.channel_type,
            phoneNumber: inbox.phone_number,
            reauthorizationRequired: inbox.reauthorization_required,
          }))
          .sort((a, b) =>
            a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1
          ),
      };
    },
    labelSection() {
      return {
        icon: 'number-symbol',
        label: 'LABELS',
        hasSubMenu: true,
        newLink: this.showNewLink(FEATURE_FLAGS.TEAM_MANAGEMENT),
        newLinkTag: 'NEW_LABEL',
        key: 'label',
        toState: frontendURL(`accounts/${this.accountId}/settings/labels`),
        toStateName: 'labels_list',
        showModalForNewItem: 'addLabel',
        dataTestid: 'sidebar-new-label-button',
        children: this.labels.map(label => ({
          id: label.id,
          label: label.title,
          color: label.color,
          truncateLabel: true,
          toState: frontendURL(
            `accounts/${this.accountId}/label/${encodeURIComponent(
              label.title
            )}`
          ),
        })),
      };
    },
    contactLabelSection() {
      return {
        icon: 'number-symbol',
        label: 'TAGGED_WITH',
        hasSubMenu: true,
        key: 'label',
        newLink: this.showNewLink(FEATURE_FLAGS.TEAM_MANAGEMENT),
        newLinkTag: 'NEW_LABEL',
        toState: frontendURL(`accounts/${this.accountId}/settings/labels`),
        toStateName: 'labels_list',
        showModalForNewItem: 'addLabel',
        children: this.labels.map(label => ({
          id: label.id,
          label: label.title,
          color: label.color,
          truncateLabel: true,
          toState: frontendURL(
            `accounts/${this.accountId}/labels/${encodeURIComponent(
              label.title
            )}/contacts`
          ),
        })),
      };
    },
    teamSection() {
      return {
        icon: 'people-team',
        label: 'TEAMS',
        hasSubMenu: true,
        newLink: this.showNewLink(FEATURE_FLAGS.TEAM_MANAGEMENT),
        newLinkTag: 'NEW_TEAM',
        key: 'team',
        toState: frontendURL(`accounts/${this.accountId}/settings/teams/new`),
        toStateName: 'settings_teams_new',
        newLinkRouteName: 'settings_teams_new',
        children: this.teams.map(team => ({
          id: team.id,
          label: team.name,
          truncateLabel: true,
          toState: frontendURL(`accounts/${this.accountId}/team/${team.id}`),
        })),
      };
    },
    foldersSection() {
      return {
        icon: 'folder',
        label: 'CUSTOM_VIEWS_FOLDER',
        hasSubMenu: true,
        showNewButton: true,
        newLink: this.conversationFilters.length === 0,
        newLinkTag: 'NEW_FILTER',
        showModalForNewItem: 'addCustomView',
        toState: frontendURL(`accounts/${this.accountId}/dashboard`),
        toStateName: 'home',
        key: 'custom_view_conversation',
        children: this.conversationFilters.map(view => ({
          id: view.id,
          label: view.name,
          truncateLabel: true,
          visibilityIcon:
            view.visibility === 'public' ? 'number-symbol' : 'lock-closed',
          toState: frontendURL(
            `accounts/${this.accountId}/custom_view/${view.id}`
          ),
          count: view.count,
        })),
      };
    },
    contactSegmentsSection() {
      return {
        icon: 'folder',
        label: 'CUSTOM_VIEWS_SEGMENTS',
        hasSubMenu: true,
        showNewButton: true,
        newLink: this.contactFilters.length === 0,
        newLinkTag: 'NEW_FILTER',
        showModalForNewItem: 'addCustomView',
        toState: frontendURL(`accounts/${this.accountId}/contacts`),
        toStateName: 'contacts_dashboard',
        key: 'custom_view_contact',
        children: this.contactFilters.map(view => ({
          id: view.id,
          label: view.name,
          truncateLabel: true,
          visibilityIcon:
            view.visibility === 'public' ? 'number-symbol' : 'lock-closed',
          toState: frontendURL(
            `accounts/${this.accountId}/contacts/custom_view/${view.id}`
          ),
          count: view.count,
        })),
      };
    },
    additionalSecondaryMenuItems() {
      let conversationMenuItems = [this.foldersSection, this.inboxSection];
      // Knowledge Bases is enabled for every account (feature flag removed),
      // so the label section is always shown.
      conversationMenuItems.push(this.labelSection);
      let contactMenuItems = [
        this.contactSegmentsSection,
        this.contactLabelSection,
      ];
      if (this.teams.length && !this.isClaudiaOnly) {
        conversationMenuItems = [this.teamSection, ...conversationMenuItems];
      }
      return {
        conversations: conversationMenuItems,
        contacts: contactMenuItems,
      };
    },
  },
  methods: {
    showAddLabelPopup() {
      this.$emit('addLabel');
    },
    showAddCustomView() {
      this.$emit('addCustomView');
    },
    toggleAccountModal() {
      this.$emit('toggleAccounts');
    },
    showNewLink(featureFlag) {
      return this.isFeatureEnabledonAccount(this.accountId, featureFlag);
    },
  },
};
</script>

<template>
  <div
    v-show="hasSecondaryMenu"
    ref="root"
    :style="sidebarStyle"
    class="relative flex flex-col w-[clamp(14rem,15vw,18rem)] flex-shrink-0 h-full overflow-hidden text-sm bg-white border-r dark:bg-slate-900 dark:border-slate-800/50 rtl:border-r-0 rtl:border-l border-slate-50"
  >
    <div v-overlay-scrollbar class="flex flex-col h-full pb-8 overflow-auto">
      <AccountContext @toggleAccounts="toggleAccountModal" />
      <transition-group
        name="menu-list"
        tag="ul"
        class="pt-2 mb-0 ml-0 list-none"
      >
        <SecondaryNavItem
          v-for="menuItem in sectionedMenuItems"
          :key="menuItem.toStateName || menuItem.toState"
          :menu-item="menuItem"
        />
        <SecondaryNavItem
          v-for="menuItem in additionalSecondaryMenuItems[menuConfig.parentNav]"
          :key="menuItem.key"
          :menu-item="menuItem"
          @addLabel="showAddLabelPopup"
          @addCustomView="showAddCustomView"
        />
      </transition-group>
    </div>
    <div
      data-testid="secondary-resize-handle"
      class="absolute top-0 end-0 z-10 w-1 h-full cursor-col-resize hover:bg-gray-gray-8 dark:hover:bg-gray-gray-11"
      @pointerdown.prevent="
        onSidebarResizeStart($event, $refs.root.getBoundingClientRect().width)
      "
    />
  </div>
</template>
